import { useState } from "react";
import { Box, Typography, Button, Menu, MenuItem } from "@mui/material";
import { keyframes } from "@mui/material/styles";
import { KeyboardArrowDown } from "@mui/icons-material";
import ShapeUploader from "./ShapeUploader";
import CoordinatesDialog from "./CoordinatesDialog";
import { useTranslation } from "react-i18next";

const shine = keyframes`
  from { transform: translateX(-100%); }
  to { transform: translateX(100%); }
`;

export default function AreaInputMethod({
  componentSource,
  boundaryMethod,
  onBoundaryMethodChange,
  onClear,
  onShapeLoaded,
  onCoordinatesApplied,
  highlight = 0,
}) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [coordsDialogOpen, setCoordsDialogOpen] = useState(false);

  const handleMethodSelect = (method) => {
    setAnchorEl(null);
    if (method === boundaryMethod) return;

    onClear();
    onBoundaryMethodChange(method);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
      <Typography
        variant="subtitle2"
        sx={{ fontWeight: 700, color: "primary.main" }}
      >
        {t("inputMethod.title")}
      </Typography>
      <Button
        // A new key restarts the sweep.
        key={highlight}
        variant="outlined"
        size="small"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        endIcon={<KeyboardArrowDown />}
        sx={{
          textTransform: "none",
          fontWeight: 600,
          borderRadius: "10px",
          overflow: "hidden",
          ...(highlight > 0 && {
            "&::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(90deg, transparent, rgba(var(--primary-rgb), 0.25), transparent)",
              transform: "translateX(-100%)",
              animation: `${shine} 1.6s ease-in-out 2`,
              pointerEvents: "none",
            },
          }),
        }}
      >
        {boundaryMethod === ""
          ? t("inputMethod.choose")
          : t(`inputMethod.${boundaryMethod}`)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <MenuItem
          selected={boundaryMethod === "draw"}
          onClick={() => handleMethodSelect("draw")}
        >
          {t("inputMethod.draw")}
        </MenuItem>
        <MenuItem
          selected={boundaryMethod === "upload"}
          onClick={() => handleMethodSelect("upload")}
        >
          {t("inputMethod.upload")}
        </MenuItem>
        <MenuItem
          selected={boundaryMethod === "coordinates"}
          onClick={() => handleMethodSelect("coordinates")}
        >
          {t("inputMethod.coordinates")}
        </MenuItem>
      </Menu>

      {boundaryMethod === "draw" && (
        <Typography
          variant="caption"
          sx={{ color: "text.secondary", textAlign: "center" }}
        >
          {t("inputMethod.drawHint")}
        </Typography>
      )}

      {boundaryMethod === "upload" && (
        <ShapeUploader
          onShapeLoaded={onShapeLoaded}
          componentSource={componentSource}
        />
      )}

      {boundaryMethod === "coordinates" && (
        <Button
          variant="outlined"
          size="small"
          onClick={() => setCoordsDialogOpen(true)}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "10px",
          }}
        >
          {t("inputMethod.openCoords")}
        </Button>
      )}

      <CoordinatesDialog
        componentSource={componentSource}
        open={coordsDialogOpen}
        onClose={() => setCoordsDialogOpen(false)}
        onCoordinatesApplied={onCoordinatesApplied}
      />
    </Box>
  );
}
